/**
 * From a document's path to the file itself.
 *
 * Handles the user granted are kept in IndexedDB, so a path in the recent list
 * or the last session still leads to its file after a reload. The browser asks
 * again before the page may read or write through a stored handle; that needs a
 * click or a key press, which is why `PermissionNeededError` exists: the caller
 * turns it into a button the user can press.
 */
import { db, randomId, type HandleRecord, type SnapshotRecord } from './db'
import {
  folderRoot,
  formatPath,
  insideFolder,
  isFolderScheme,
  parsePath,
  type ParsedPath,
} from './paths'

/** The File System Access API, as Chromium-based browsers ship it. */
export const hasFileSystemAccess =
  typeof window !== 'undefined' && 'showOpenFilePicker' in window && 'showDirectoryPicker' in window

/** A failure carrying the code the shared error explanations know. */
export class PlatformError extends Error {
  constructor(
    readonly code: string,
    message: string
  ) {
    // The code goes in the message as well: the renderer finds it there, as it
    // found Node's codes in the desktop's IPC errors.
    super(`${code}: ${message}`)
    this.name = 'PlatformError'
  }
}

/** The browser wants the user's go-ahead before this handle may be used. */
export class PermissionNeededError extends PlatformError {
  constructor(
    readonly handle: FileSystemHandle,
    readonly mode: FileSystemPermissionMode
  ) {
    super(
      'EGRANT',
      `the browser needs your permission to ${mode === 'read' ? 'read' : 'change'} ${handle.name}.`
    )
    this.name = 'PermissionNeededError'
  }
}

/** A DOMException from the File System Access API, as the code the UI explains. */
export function toPlatformError(err: unknown, what: string): Error {
  if (err instanceof PlatformError) return err
  const name = (err as DOMException | undefined)?.name
  const message = err instanceof Error ? err.message : String(err)
  switch (name) {
    case 'NotFoundError':
      return new PlatformError('ENOENT', `${what} is no longer there.`)
    case 'NotAllowedError':
    case 'SecurityError':
      return new PlatformError('EACCES', `the browser did not allow access to ${what}.`)
    case 'TypeMismatchError':
      return new PlatformError('EISDIR', `${what} is a folder, not a file.`)
    case 'NoModificationAllowedError':
      return new PlatformError('EPERM', `${what} could not be changed. It may be locked.`)
    case 'QuotaExceededError':
      return new PlatformError('ENOSPC', 'there is not enough space.')
    case 'InvalidModificationError':
    case 'InvalidStateError':
      return new PlatformError('EBUSY', `${what} changed while it was being written.`)
    default:
      return err instanceof Error ? err : new Error(message)
  }
}

/** True while the page may ask the browser for something a click must start. */
function hasUserActivation(): boolean {
  const ua = (navigator as { userActivation?: { isActive: boolean } }).userActivation
  // A browser without the API: try, and let the browser refuse if it must.
  return ua ? ua.isActive : true
}

/**
 * Makes sure the page may use a handle, asking the user when it can. Throws
 * PermissionNeededError when the browser would ask but no click is at hand.
 */
export async function ensurePermission(
  handle: FileSystemHandle,
  mode: FileSystemPermissionMode,
  opts: { ask?: boolean } = {}
): Promise<void> {
  const query = (handle as { queryPermission?: FileSystemHandle['queryPermission'] })
    .queryPermission
  if (!query) return
  const state = await handle.queryPermission({ mode })
  if (state === 'granted') return
  if (state === 'denied') throw new PlatformError('EACCES', `access to ${handle.name} was refused.`)
  if (opts.ask === false || !hasUserActivation()) throw new PermissionNeededError(handle, mode)
  const answer = await handle.requestPermission({ mode })
  if (answer !== 'granted')
    throw new PlatformError('EACCES', `access to ${handle.name} was refused.`)
}

// --- the registry -----------------------------------------------------------

/** Read once per page, then kept here: records change only through this module. */
let records: Map<string, HandleRecord> | null = null

async function allRecords(): Promise<Map<string, HandleRecord>> {
  if (!records) {
    const list = await (await db()).getAll('handles')
    records = new Map(list.map((r) => [r.id, r]))
  }
  return records
}

async function putRecord(rec: HandleRecord): Promise<void> {
  ;(await allRecords()).set(rec.id, rec)
  await (await db()).put('handles', rec)
}

export async function handleRecord(id: string): Promise<HandleRecord | undefined> {
  return (await allRecords()).get(id)
}

/** Marks a handle used, so pruning keeps it. Fire and forget. */
function touch(rec: HandleRecord): void {
  rec.usedAt = Date.now()
  void db().then((d) => d.put('handles', rec))
}

/**
 * An id for a handle: the one it already has when the same file or folder was
 * granted before, so opening it twice does not make two tabs of it.
 */
async function idFor(handle: FileSystemFileHandle | FileSystemDirectoryHandle): Promise<string> {
  for (const rec of (await allRecords()).values()) {
    if (rec.kind !== handle.kind) continue
    try {
      if (await rec.handle.isSameEntry(handle)) {
        // The newer handle: it carries the permission just granted.
        rec.handle = handle
        rec.name = handle.name
        await putRecord({ ...rec, usedAt: Date.now() })
        return rec.id
      }
    } catch {
      // A handle to something that has gone: not this one.
    }
  }
  const id = randomId()
  await putRecord({ id, kind: handle.kind, name: handle.name, handle, usedAt: Date.now() })
  return id
}

/** The folder opened in this tab, consulted so a file inside it gets its folder path. */
let openFolder: string | null = null

export function setOpenFolder(root: string | null): void {
  openFolder = root
}

export function getOpenFolder(): string | null {
  return openFolder
}

/**
 * The path for a file handle: inside the open folder if it is in it, so the
 * file the tree shows and the same file chosen with Open are one tab.
 */
export async function pathForFileHandle(handle: FileSystemFileHandle): Promise<string> {
  const root = openFolder ? parsePath(openFolder) : null
  if (root?.scheme === 'ws') {
    const rec = await handleRecord(root.id)
    if (rec?.kind === 'directory') {
      try {
        const inside = await (rec.handle as FileSystemDirectoryHandle).resolve(handle)
        if (inside) return formatPath('ws', root.id, [root.segments[0], ...inside])
      } catch {
        // Not resolvable from here: a file on its own, then.
      }
    }
  }
  return formatPath('fh', await idFor(handle), [handle.name])
}

export async function pathForDirectoryHandle(handle: FileSystemDirectoryHandle): Promise<string> {
  return formatPath('ws', await idFor(handle), [handle.name])
}

// --- snapshots: files and folders read without a handle ----------------------

/** Folders read through <input webkitdirectory> or a drop, for this page's life. */
const snapshotFolders = new Map<string, { name: string; files: Map<string, File> }>()

/** Registers a folder of File objects, each named by its path inside the folder. */
export function registerSnapshotFolder(
  name: string,
  files: Array<{ path: string; file: File }>
): string {
  const id = randomId()
  snapshotFolders.set(id, { name, files: new Map(files.map((f) => [f.path, f.file])) })
  return formatPath('snap', id, [name])
}

export function snapshotFolder(id: string): { name: string; files: Map<string, File> } | undefined {
  return snapshotFolders.get(id)
}

const MAX_COPIES = 20
const MAX_COPY_BYTES = 5 * 1024 * 1024

/** Keeps the text of a file read without a handle, so Open Recent can reopen it. */
export async function storeCopy(rec: Omit<SnapshotRecord, 'id' | 'usedAt'>): Promise<string> {
  const id = randomId()
  const d = await db()
  await d.put('snapshots', { ...rec, id, usedAt: Date.now() })
  // The newest are kept, up to a count and a size.
  const all = (await d.getAll('snapshots')).sort((a, b) => b.usedAt - a.usedAt)
  let bytes = 0
  for (const [i, s] of all.entries()) {
    bytes += s.size
    if (i > 0 && (i >= MAX_COPIES || bytes > MAX_COPY_BYTES)) await d.delete('snapshots', s.id)
  }
  return formatPath('copy', id, [rec.name])
}

export async function readCopy(id: string): Promise<SnapshotRecord | undefined> {
  return (await db()).get('snapshots', id)
}

export async function updateCopy(id: string, patch: Partial<SnapshotRecord>): Promise<void> {
  const d = await db()
  const rec = await d.get('snapshots', id)
  if (rec) await d.put('snapshots', { ...rec, ...patch, usedAt: Date.now() })
}

// --- resolution -------------------------------------------------------------

/** A directory handle for a `ws:` path: its root, or a folder inside it. */
export async function directoryFor(
  path: string,
  mode: FileSystemPermissionMode,
  opts: { create?: boolean; ask?: boolean } = {}
): Promise<FileSystemDirectoryHandle> {
  const p = parsePath(path)
  if (!p || p.scheme !== 'ws')
    throw new PlatformError('ENOENT', `${path} is not in an open folder.`)
  const rec = await handleRecord(p.id)
  if (!rec || rec.kind !== 'directory')
    throw new PlatformError('ENOENT', 'that folder is no longer known.')
  const root = rec.handle as FileSystemDirectoryHandle
  await ensurePermission(root, mode, opts)
  touch(rec)
  const inside = insideFolder(p)
  if (!inside) throw new PlatformError('EACCES', `${path} is not inside the open folder.`)
  let dir = root
  try {
    for (const name of inside) dir = await dir.getDirectoryHandle(name, { create: opts.create })
  } catch (err) {
    throw toPlatformError(err, inside[inside.length - 1] ?? root.name)
  }
  return dir
}

/** A file handle for a `ws:` or `fh:` path. */
export async function fileHandleFor(
  path: string,
  mode: FileSystemPermissionMode,
  opts: { create?: boolean; ask?: boolean } = {}
): Promise<FileSystemFileHandle> {
  const p = parsePath(path)
  if (!p) throw new PlatformError('ENOENT', `${path} cannot be opened in a browser.`)
  if (p.scheme === 'fh') {
    const rec = await handleRecord(p.id)
    if (!rec || rec.kind !== 'file')
      throw new PlatformError('ENOENT', 'that file is no longer known.')
    await ensurePermission(rec.handle, mode, opts)
    touch(rec)
    return rec.handle as FileSystemFileHandle
  }
  if (p.scheme === 'ws') {
    const inside = insideFolder(p)
    if (!inside || inside.length === 0) {
      throw new PlatformError('EISDIR', `${path} is not a file in the open folder.`)
    }
    const name = inside[inside.length - 1]
    const dir = await directoryFor(
      formatPath('ws', p.id, [p.segments[0], ...inside.slice(0, -1)]),
      mode,
      opts
    )
    try {
      return await dir.getFileHandle(name, { create: opts.create })
    } catch (err) {
      throw toPlatformError(err, name)
    }
  }
  throw new PlatformError('EROFS', `${p.segments.at(-1) ?? path} has no file to write to.`)
}

/** The File behind a path, for reading: from a handle, a snapshot, or nowhere. */
export async function fileFor(path: string, opts: { ask?: boolean } = {}): Promise<File> {
  const p = parsePath(path)
  if (!p) throw new PlatformError('ENOENT', `${path} cannot be opened in a browser.`)
  if (p.scheme === 'snap') {
    const folder = snapshotFolders.get(p.id)
    const inside = insideFolder(p)
    const file = folder && inside ? folder.files.get(inside.join('/')) : undefined
    if (!file) throw new PlatformError('ENOENT', `${p.segments.at(-1)} is no longer there.`)
    return file
  }
  const handle = await fileHandleFor(path, 'read', opts)
  try {
    return await handle.getFile()
  } catch (err) {
    throw toPlatformError(err, handle.name)
  }
}

/** The folder a document is in, for relative links: null when there is none to read. */
export function folderOf(p: ParsedPath): string | null {
  if (!isFolderScheme(p.scheme)) return null
  const root = folderRoot(p)
  const inside = insideFolder(p)
  if (!root || !inside) return null
  return [root, ...inside.slice(0, -1)].join('/')
}

/**
 * Forgets handles nothing refers to any more: not in the recent list, the last
 * session or the open folder, and unused for a week.
 */
export async function pruneHandles(referenced: string[]): Promise<void> {
  const keep = new Set(referenced.map((p) => parsePath(p)?.id).filter(Boolean))
  const weekAgo = Date.now() - 7 * 24 * 3600 * 1000
  const d = await db()
  for (const rec of (await allRecords()).values()) {
    if (keep.has(rec.id) || rec.usedAt > weekAgo) continue
    records?.delete(rec.id)
    await d.delete('handles', rec.id)
  }
}
